import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaHome, FaPlus, FaCrown, FaUser, FaSignOutAlt,
  FaFolder, FaMagic,
  FaChevronDown, FaShoppingCart, FaUsers,
} from 'react-icons/fa';
import { serverURL } from '../constants';
import axios from 'axios';
import DarkModeToggle from './DarkModeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import MobileBottomNav from './MobileBottomNav';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import { useCreateCourseChoice } from '../context/CreateCourseChoiceContext';
import { isInsideRoleShell } from '../shared/ui/roleShell';
import { useOrganization } from '../shared/branding/OrganizationContext';
import OrgBrandLogo from '../shared/branding/OrgBrandLogo';

function ResponsiveLabel({ label, shortLabel }) {
  const short = shortLabel || label;
  return (
    <>
      <span className="xl:hidden">{short}</span>
      <span className="hidden xl:inline">{label}</span>
    </>
  );
}

const AI_STUDIO_PATHS = ['/projects', '/genui', '/marketplace'];

function AiStudioDropdown({ open, onNavigate, isPathActive, t }) {
  const items = [
    {
      key: 'projects',
      path: '/projects',
      icon: FaFolder,
      title: t('header.aiStudio.projects'),
      shortTitle: t('header.quickAccess.projects'),
      desc: t('header.aiStudio.projectsDesc'),
      iconBg: 'bg-slate-100 dark:bg-slate-800',
      iconColor: 'text-slate-600 dark:text-slate-300',
    },
    {
      key: 'studio',
      path: '/genui',
      icon: FaMagic,
      title: t('header.aiStudio.studio'),
      shortTitle: t('header.quickAccess.studio'),
      desc: t('header.aiStudio.studioDesc'),
      iconBg: 'bg-slate-100 dark:bg-slate-800',
      iconColor: 'text-slate-600 dark:text-slate-300',
    },
    {
      key: 'marketplace',
      path: '/marketplace',
      icon: FaShoppingCart,
      title: t('header.aiStudio.marketplace'),
      shortTitle: t('header.quickAccess.marketplace'),
      desc: t('header.aiStudio.marketplaceDesc'),
      iconBg: 'bg-slate-100 dark:bg-slate-800',
      iconColor: 'text-slate-600 dark:text-slate-300',
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.15 }}
          className="absolute end-0 top-full mt-2 w-72 rounded-xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-700 py-1.5 z-[60]"
          role="menu"
          aria-label={t('header.aiStudio.menu')}
        >
          {items.map((item, index) => {
            const active = isPathActive(item.path);
            const Icon = item.icon;
            return (
              <React.Fragment key={item.key}>
                {index > 0 && (
                  <div className="mx-3 border-t border-slate-100 dark:border-slate-800" role="separator" />
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => onNavigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-start transition-colors group ${
                    active
                      ? 'bg-blue-50/80 dark:bg-blue-950/25'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/80'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg ${item.iconBg} flex items-center justify-center shrink-0`}>
                    <Icon className={active ? 'text-blue-600 dark:text-blue-400' : item.iconColor} size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      active ? 'text-blue-700 dark:text-blue-300' : 'text-slate-800 dark:text-slate-100'
                    }`}>
                      <span className="xl:hidden">{item.shortTitle || item.title}</span>
                      <span className="hidden xl:inline">{item.title}</span>
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug line-clamp-2">
                      {item.desc}
                    </p>
                  </div>
                  <FaChevronDown
                    className={`shrink-0 -rotate-90 rtl:rotate-90 transition-opacity ${
                      active ? 'text-blue-500 opacity-100' : 'text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100'
                    }`}
                    size={10}
                    aria-hidden
                  />
                </button>
              </React.Fragment>
            );
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AccountMenuDropdown({ open, sections, onNavigate, onLogout, isActive, t }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.15 }}
          className="absolute end-0 top-full mt-2 w-56 rounded-xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-700 py-1.5 z-[60]"
          role="menu"
        >
          {sections.map((section, si) => (
            <React.Fragment key={section.key}>
              {si > 0 && <div className="my-1 border-t border-slate-100 dark:border-slate-800" role="separator" />}
              {section.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  onClick={() => (item.action === 'logout' ? onLogout() : onNavigate(item.path))}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-start transition-colors ${
                    item.action === 'logout'
                      ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30'
                      : isActive(item.path)
                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
                        : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <item.icon className="w-3.5 h-3.5 shrink-0 opacity-80" aria-hidden />
                  {item.label}
                </button>
              ))}
            </React.Fragment>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Slim top row — site info links on branded gradient strip. */
function TopUtilityBar({ items, isActive, onNavigate, t }) {
  if (!items.length) return null;

  return (
    <nav
      className="top-utility-bar hidden lg:block relative overflow-hidden border-b border-blue-700/40 dark:border-blue-900/60 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 dark:from-blue-950 dark:via-slate-900 dark:to-blue-950 text-white"
      aria-label={t('header.quickAccess.label')}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.12),transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top_right,rgba(96,165,250,0.08),transparent_55%)]"
        aria-hidden
      />
      <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-end gap-x-1 gap-y-1 py-1.5 min-h-[2rem] flex-wrap">
          {items.map((item, index) => {
            const active = isActive(item.path);
            return (
              <React.Fragment key={item.key}>
                {index > 0 && (
                  <span className="top-utility-bar__dot mx-1.5 text-[10px] select-none" aria-hidden>
                    ·
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onNavigate(item.path)}
                  aria-current={active ? 'page' : undefined}
                  className={`top-utility-bar__link text-xs font-medium whitespace-nowrap transition-colors rounded-md px-1.5 py-0.5 ${
                    active ? 'top-utility-bar__link--active font-semibold' : ''
                  }`}
                >
                  {item.label}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

const Header = ({ isHome }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, organization } = useOrganization();
  const isTenantHost = Boolean(organization?.tenant);
  const orgPrimary = branding?.primaryColor || '';
  const tenantCtaStyle = isTenantHost && orgPrimary
    ? { backgroundColor: orgPrimary }
    : undefined;
  const tenantCtaClass = isTenantHost
    ? 'text-white hover:opacity-90'
    : 'bg-blue-600 hover:bg-blue-700 text-white';
  const { openCreateCourseChoice } = useCreateCourseChoice();
  const [admin, setAdmin] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [aiStudioOpen, setAiStudioOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const aiStudioRef = useRef(null);
  const accountRef = useRef(null);
  const hideChrome = isInsideRoleShell(location.pathname);

  useEffect(() => {
    if (hideChrome) return undefined;
    const handler = (e) => {
      if (aiStudioRef.current && !aiStudioRef.current.contains(e.target)) setAiStudioOpen(false);
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [hideChrome]);

  useEffect(() => {
    setAiStudioOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (hideChrome) return;
    if (isHome && sessionStorage.getItem('uid') === null) navigate('/signin');

    async function checkAdminStatus() {
      try {
        const email = sessionStorage.getItem('email');
        if (!email) { setAdmin(false); return; }
        const [adminResponse] = await Promise.all([
          axios.get(`${serverURL}/api/admins`),
          axios.get(`${serverURL}/api/dashboard`),
        ]);
        const isAdmin = (adminResponse.data.adminEmails || []).includes(email);
        if (isAdmin) {
          sessionStorage.setItem('adminEmail', email);
          setAdmin(true);
        } else {
          setAdmin(false);
          sessionStorage.removeItem('adminEmail');
        }
      } catch (error) {
        const status = error.response?.status;
        // Auth misses and API outages are expected noise — never alarm users/logs.
        if (
          status !== 401
          && status !== 403
          && status !== 502
          && status !== 503
          && status !== 504
          && error.response
        ) {
          logger.warn('Error checking admin status:', error?.message || error);
        }
        setAdmin(false);
        sessionStorage.removeItem('adminEmail');
      }
    }

    if (sessionStorage.getItem('uid')) checkAdminStatus();
    else { setAdmin(false); sessionStorage.removeItem('adminEmail'); }
  }, [isHome, navigate, hideChrome]);

  useEffect(() => {
    if (hideChrome) return undefined;
    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hideChrome]);

  useEffect(() => {
    if (hideChrome) return undefined;
    const checkDarkMode = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [hideChrome]);

  if (hideChrome) {
    return null;
  }

  const isLoggedIn = sessionStorage.getItem('uid') !== null;
  const userRole = sessionStorage.getItem('role') || 'student';
  const isTeacher = ['admin', 'instructor', 'teacher'].includes(userRole);
  const isActive = (path) => location.pathname === path;
  const isStudioPathActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isAiStudioActive = AI_STUDIO_PATHS.some((p) => isStudioPathActive(p));
  const isCreateOrGenUI = ['/genui', '/create-choice'].includes(location.pathname) || location.pathname === '/create';

  const classesLabel = isTeacher ? t('header.nav.myClasses') : t('header.nav.myLearning');
  const classesShort = isTeacher ? t('header.nav.myClassesShort') : t('header.nav.myLearningShort');

  const topUtilityItems = isTenantHost
    ? [
        { key: 'support', path: '/support', label: t('header.nav.support') },
        { key: 'faq', path: '/faq', label: t('header.nav.faq') },
        { key: 'contact', path: '/contact', label: t('header.nav.contact') },
      ]
    : [
        ...(isLoggedIn
          ? [{ key: 'courseIdeas', path: '/course-ideas', label: t('header.nav.courseIdeasShort') }]
          : []),
        { key: 'features', path: '/features', label: t('header.nav.features') },
        { key: 'platform', path: '/platform', label: t('header.nav.platform', 'Platform') },
        { key: 'about', path: '/about', label: t('header.nav.about') },
        { key: 'contact', path: '/contact', label: t('header.nav.contact') },
        { key: 'support', path: '/support', label: t('header.nav.support') },
        { key: 'faq', path: '/faq', label: t('header.nav.faq') },
        { key: 'pricing', path: '/pricing', label: t('header.nav.pricing') },
      ];

  const accountSections = [
    ...(admin && isLoggedIn
      ? [{
          key: 'admin',
          items: [{ key: 'admin', label: t('header.nav.admin'), path: '/admin/dashboard', icon: FaCrown }],
        }]
      : []),
    {
      key: 'session',
      items: [
        { key: 'profile', label: t('header.nav.profile'), path: '/learn/profile', icon: FaUser },
        { key: 'logout', label: t('header.auth.logout'), path: '', icon: FaSignOutAlt, action: 'logout' },
      ],
    },
  ];

  const accountPaths = accountSections.flatMap((s) => s.items.map((i) => i.path).filter(Boolean));
  const isAccountActive = accountPaths.some((p) => isActive(p));

  const navLinkClass = (active) =>
    `flex items-center gap-1.5 shrink-0 px-3 py-2 rounded-lg font-medium transition-colors text-sm min-h-[2.25rem] ${
      active
        ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300'
        : 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800'
    }`;

  const aiStudioBtnClass =
    isAiStudioActive || aiStudioOpen
      ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-800'
      : 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800';

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/');
    setAccountOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-200 border-b ${
          isScrolled
            ? 'bg-white/95 dark:bg-slate-950/95 backdrop-blur-md shadow-sm border-slate-200 dark:border-slate-800'
            : 'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800'
        }`}
      >
        <TopUtilityBar
          items={topUtilityItems}
          isActive={isActive}
          onNavigate={navigate}
          t={t}
        />

        <div className="max-w-7xl mx-auto px-3 sm:px-5 lg:px-8">
          <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 sm:gap-4 h-14 sm:h-16 min-w-0">
            {/* Brand — left */}
            <button
              type="button"
              onClick={() => navigate(isLoggedIn ? '/home' : '/')}
              className="justify-self-start shrink-0 flex items-center max-w-[min(11.5rem,46vw)] sm:max-w-[13.5rem] rounded-lg hover:opacity-90 transition-opacity focus-visible:ring-2 focus-visible:ring-[var(--org-primary,#2563eb)]"
              aria-label={branding?.companyName || organization?.name || 'Coursolo.ai'}
            >
              <OrgBrandLogo
                branding={branding}
                dark={isDarkMode}
                className="h-10 sm:h-11 w-auto object-contain max-h-10 sm:max-h-11"
                alt={branding?.companyName || organization?.name || 'Coursolo.ai'}
              />
            </button>

            {/* Primary nav — center (desktop) */}
            <nav
              className="hidden lg:flex items-center justify-center gap-0.5 xl:gap-1 min-w-0"
              aria-label={t('header.nav.navigation')}
            >
              {isLoggedIn ? (
                <>
                  <button
                    type="button"
                    onClick={() => navigate('/home')}
                    title={t('header.nav.home')}
                    className={navLinkClass(isActive('/home'))}
                  >
                    <FaHome className="w-3.5 h-3.5 shrink-0" aria-hidden />
                    <ResponsiveLabel label={t('header.nav.home')} shortLabel={t('header.nav.homeShort')} />
                  </button>

                  <div className="relative shrink-0" ref={aiStudioRef}>
                    <button
                      type="button"
                      aria-haspopup="menu"
                      aria-expanded={aiStudioOpen}
                      onClick={() => setAiStudioOpen((v) => !v)}
                      title={t('header.aiStudio.menu')}
                      className={`flex items-center gap-1.5 shrink-0 px-3 py-2 rounded-lg font-semibold transition-colors text-sm min-h-[2.25rem] ${aiStudioBtnClass}`}
                    >
                      <FaMagic className="w-3.5 h-3.5 shrink-0" aria-hidden />
                      <ResponsiveLabel label={t('header.aiStudio.menu')} shortLabel="Studio" />
                      <FaChevronDown className={`w-2.5 h-2.5 transition-transform ${aiStudioOpen ? 'rotate-180' : ''}`} aria-hidden />
                    </button>
                    <AiStudioDropdown
                      open={aiStudioOpen}
                      onNavigate={(path) => { navigate(path); setAiStudioOpen(false); }}
                      isPathActive={isStudioPathActive}
                      t={t}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate('/my-classes')}
                    title={classesLabel}
                    className={navLinkClass(isStudioPathActive('/my-classes'))}
                  >
                    <FaUsers className="w-3.5 h-3.5 shrink-0" aria-hidden />
                    <ResponsiveLabel label={classesLabel} shortLabel={classesShort} />
                  </button>
                </>
              ) : isTenantHost ? (
                <>
                  <button type="button" onClick={() => navigate('/signin')} className={navLinkClass(isActive('/signin'))}>
                    {t('header.nav.signIn')}
                  </button>
                  <button type="button" onClick={() => navigate('/signup')} className={navLinkClass(isActive('/signup'))}>
                    {t('header.nav.join', 'Join')}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => navigate('/features')} className={navLinkClass(isActive('/features'))}>
                    {t('header.nav.features')}
                  </button>
                  <button type="button" onClick={() => navigate('/platform')} className={navLinkClass(isActive('/platform'))}>
                    {t('header.nav.platform', 'Platform')}
                  </button>
                  <button type="button" onClick={() => navigate('/pricing')} className={navLinkClass(isActive('/pricing'))}>
                    {t('header.nav.pricing')}
                  </button>
                </>
              )}
            </nav>

            {/* Actions — right */}
            <div className="justify-self-end flex items-center shrink-0 gap-1 sm:gap-1.5 min-w-0">
              <div className="hidden lg:flex items-center gap-1 shrink-0">
                <LanguageSwitcher compact />
                <DarkModeToggle />
              </div>

              {isLoggedIn ? (
                <>
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => openCreateCourseChoice()}
                    title={t('header.nav.createCourse')}
                    className={`hidden lg:flex items-center gap-1.5 shrink-0 px-3.5 py-2 rounded-lg font-semibold text-sm min-h-[2.25rem] transition-colors ${
                      isCreateOrGenUI
                        ? `${tenantCtaClass} ring-2 ring-blue-300/60 dark:ring-blue-700`
                        : tenantCtaClass
                    }`}
                    style={tenantCtaStyle}
                  >
                    <FaPlus className="w-3.5 h-3.5 shrink-0" aria-hidden />
                    <ResponsiveLabel label={t('header.nav.createCourse')} shortLabel={t('header.nav.createCourseShort')} />
                  </motion.button>

                  <div className="relative hidden lg:block shrink-0" ref={accountRef}>
                    <button
                      type="button"
                      aria-haspopup="menu"
                      aria-expanded={accountOpen}
                      aria-label={t('header.nav.account')}
                      onClick={() => setAccountOpen((v) => !v)}
                      className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors min-h-[2.25rem] ${
                        isAccountActive || accountOpen
                          ? 'bg-slate-100 dark:bg-slate-800 text-blue-700 dark:text-blue-300'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <span className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-950/60 flex items-center justify-center">
                        <FaUser className="w-3 h-3 text-blue-600 dark:text-blue-400" aria-hidden />
                      </span>
                      <FaChevronDown className={`w-2.5 h-2.5 transition-transform ${accountOpen ? 'rotate-180' : ''}`} aria-hidden />
                    </button>
                    <AccountMenuDropdown
                      open={accountOpen}
                      sections={accountSections}
                      onNavigate={(path) => { navigate(path); setAccountOpen(false); }}
                      onLogout={handleLogout}
                      isActive={isActive}
                      t={t}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="hidden lg:flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => navigate('/signin')}
                      className="px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400"
                    >
                      {t('header.auth.signIn')}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      className={`px-4 py-2 text-sm font-semibold rounded-lg ${tenantCtaClass}`}
                      style={tenantCtaStyle}
                    >
                      {isTenantHost ? t('header.nav.join', 'Join') : t('header.auth.getStarted')}
                    </button>
                  </div>
                  <div className="lg:hidden flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => navigate('/signin')}
                      className="px-2.5 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 min-h-[2.25rem]"
                    >
                      {t('header.auth.signIn')}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg min-h-[2.25rem] shadow-sm ${tenantCtaClass}`}
                      style={tenantCtaStyle}
                    >
                      {isTenantHost ? t('header.nav.join', 'Join') : t('header.auth.getStartedShort', 'Start')}
                    </button>
                  </div>
                </>
              )}

              <div className="lg:hidden flex items-center gap-1 shrink-0">
                <LanguageSwitcher compact />
                <DarkModeToggle />
              </div>
            </div>
          </div>
        </div>
      </header>

      <MobileBottomNav
        isLoggedIn={isLoggedIn}
        admin={admin}
        onLogout={handleLogout}
        onCreateCoursePress={openCreateCourseChoice}
      />

      <div className="h-14 sm:h-16 lg:h-[calc(4rem+2rem)]" aria-hidden />
    </>
  );
};

export default Header;
